import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { FileText, DollarSign, AlertCircle, RefreshCw, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Firebase
import { db } from '../firebase';
import {
  collection,
  getDocs,
} from 'firebase/firestore';

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    setLoading(true);
    try {
      // 🔹 Load all needed collections in parallel
      const [invoicesSnap, customersSnap, recurringSnap, businessesSnap] =
        await Promise.all([
          getDocs(collection(db, 'invoices')),
          getDocs(collection(db, 'customers')),
          getDocs(collection(db, 'recurringSchedules')),
          getDocs(collection(db, 'businesses')),
        ]);

      const invoices = invoicesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const customers = customersSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const recurring = recurringSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const businesses = businessesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // 🔹 Filter out proforma invoices from dashboard stats
      const nonProformaInvoices = invoices.filter(inv => inv.status !== 'proforma');

      // 🔹 Basic counts
      const total_invoices = nonProformaInvoices.length;
      const customers_count = customers.length;

      const paidInvoices = nonProformaInvoices.filter(inv => inv.status === 'paid');
      const unpaidInvoices = nonProformaInvoices.filter(inv => inv.status === 'unpaid');
      const overdueInvoices = nonProformaInvoices.filter(inv => inv.status === 'overdue');

      const paid_count = paidInvoices.length;
      const unpaid_count = unpaidInvoices.length;
      const overdue_count = overdueInvoices.length;

      // 🔹 Revenue numbers
      const safeNum = (v) => (typeof v === 'number' ? v : Number(v || 0));

      const total_revenue = paidInvoices.reduce(
        (sum, inv) => sum + safeNum(inv.total),
        0
      );

      const pending_amount = [...unpaidInvoices, ...overdueInvoices].reduce(
        (sum, inv) => sum + safeNum(inv.total),
        0
      );

      // 🔹 Revenue by business (only paid invoices, usually)
      const revenueByBusinessMap = {};

      paidInvoices.forEach(inv => {
        const bid = inv.business_id;
        if (!bid) return;
        if (!revenueByBusinessMap[bid]) {
          revenueByBusinessMap[bid] = 0;
        }
        revenueByBusinessMap[bid] += safeNum(inv.total);
      });

      const business_revenue = Object.entries(revenueByBusinessMap).map(
        ([businessId, revenue]) => {
          const biz = businesses.find(b => b.id === businessId);
          return {
            business_id: businessId,
            business_name: biz?.name || 'Unknown business',
            revenue,
          };
        }
      );

      // 🔹 Recurring schedules count (only active ones)
      const recurring_schedules_count = recurring.filter(r => r.is_active).length;

      setStats({
        total_invoices,
        total_revenue,
        pending_amount,
        customers_count,
        paid_count,
        unpaid_count,
        overdue_count,
        business_revenue,
        recurring_schedules_count,
      });
    } catch (error) {
      console.error('Failed to fetch stats from Firestore:', error);
      setStats(null);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 md:p-12">
        <h1 className="text-4xl md:text-5xl font-bold mb-12 tracking-tight">Dashboard</h1>
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  const statCards = [
    {
      title: 'Total Invoices',
      value: stats?.total_invoices || 0,
      icon: FileText,
      color: 'text-primary',
      bgColor: 'bg-primary/10',
    },
    {
      title: 'Total Revenue',
      value: `₹${(stats?.total_revenue || 0).toFixed(2)}`,
      icon: DollarSign,
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-100',
    },
    {
      title: 'Pending Amount',
      value: `₹${(stats?.pending_amount || 0).toFixed(2)}`,
      icon: AlertCircle,
      color: 'text-amber-600',
      bgColor: 'bg-amber-100',
    },
    {
      title: 'Customers',
      value: stats?.customers_count || 0,
      icon: Users,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100',
    },
  ];

  return (
    <div className="p-8 md:p-12" data-testid="dashboard">
      <h1 className="text-4xl md:text-5xl font-bold mb-12 tracking-tight">Dashboard</h1>

      {/* Stats Grid - Bento Layout */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
        {statCards.map((stat, index) => (
          <Card
            key={index}
            className="p-8 hover:shadow-hover transition-all cursor-pointer border-border/50"
            data-testid={`stat-card-${stat.title.toLowerCase().replace(/\s/g, '-')}`}
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-2">
                  {stat.title}
                </p>
                <h3
                  className="text-3xl font-bold tracking-tight"
                  style={{ fontFamily: 'JetBrains Mono, monospace' }}
                >
                  {stat.value}
                </h3>
              </div>
              <div className={`${stat.bgColor} ${stat.color} p-3 rounded-xl`}>
                <stat.icon className="w-6 h-6" />
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Invoice Status Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-12">
        <Card
          className="p-8 hover:shadow-hover transition-all border-border/50"
          data-testid="paid-invoices-card"
        >
          <div className="flex items-center gap-4">
            <div className="bg-emerald-100 p-4 rounded-xl">
              <FileText className="w-8 h-8 text-emerald-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Paid Invoices</p>
              <h3
                className="text-4xl font-bold text-emerald-600"
                style={{ fontFamily: 'JetBrains Mono, monospace' }}
              >
                {stats?.paid_count || 0}
              </h3>
            </div>
          </div>
        </Card>

        <Card
          className="p-8 hover:shadow-hover transition-all border-border/50"
          data-testid="unpaid-invoices-card"
        >
          <div className="flex items-center gap-4">
            <div className="bg-amber-100 p-4 rounded-xl">
              <FileText className="w-8 h-8 text-amber-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Unpaid Invoices</p>
              <h3
                className="text-4xl font-bold text-amber-600"
                style={{ fontFamily: 'JetBrains Mono, monospace' }}
              >
                {stats?.unpaid_count || 0}
              </h3>
            </div>
          </div>
        </Card>

        <Card
          className="p-8 hover:shadow-hover transition-all border-border/50"
          data-testid="overdue-invoices-card"
        >
          <div className="flex items-center gap-4">
            <div className="bg-red-100 p-4 rounded-xl">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Overdue Invoices</p>
              <h3
                className="text-4xl font-bold text-red-600"
                style={{ fontFamily: 'JetBrains Mono, monospace' }}
              >
                {stats?.overdue_count || 0}
              </h3>
            </div>
          </div>
        </Card>
      </div>

      {/* Revenue by Business & Recurring */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card
          className="p-8 border-border/50"
          data-testid="business-revenue-card"
        >
          <h3 className="text-2xl font-bold mb-6">Revenue by Business</h3>
          {stats?.business_revenue && stats.business_revenue.length > 0 ? (
            <div className="space-y-4">
              {stats.business_revenue.map((business, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-4 bg-muted rounded-lg"
                >
                  <span className="font-medium text-foreground">
                    {business.business_name}
                  </span>
                  <span
                    className="text-xl font-bold text-primary"
                    style={{ fontFamily: 'JetBrains Mono, monospace' }}
                  >
                    ₹{business.revenue.toFixed(2)}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground">No revenue data available</p>
          )}
        </Card>

        <Card
          className="p-8 border-border/50"
          data-testid="recurring-schedules-card"
        >
          <h3 className="text-2xl font-bold mb-6">Recurring Schedules</h3>
          <div className="flex items-center gap-4">
            <div className="bg-primary/10 p-6 rounded-xl">
              <RefreshCw className="w-10 h-10 text-primary" />
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">
                Active Schedules
              </p>
              <h3
                className="text-5xl font-bold text-primary"
                style={{ fontFamily: 'JetBrains Mono, monospace' }}
              >
                {stats?.recurring_schedules_count || 0}
              </h3>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
